/**
 * Firebase User Service
 * Handles Firestore user profile operations
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';
import { UserProfile, Address, AddressFormData } from '@/types/user.types';

/**
 * Generate a unique ID
 * Uses crypto.randomUUID if available, falls back to timestamp-based ID
 */
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: timestamp + random string
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Get user profile by ID
 * @param userId - Firebase Auth user ID
 * @returns UserProfile or null if not found
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    
    if (!userDoc.exists()) {
      return null;
    }

    return {
      id: userDoc.id,
      ...userDoc.data(),
    } as UserProfile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw new Error('Failed to fetch user profile');
  }
};

/**
 * Get total count of registered users
 */
export const getUsersCount = async (): Promise<number> => {
  const snapshot = await getDocs(collection(db, COLLECTIONS.USERS));
  return snapshot.size;
};

/**
 * Get all user profiles
 * @returns Array of UserProfile sorted by createdAt descending
 */
export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    })) as UserProfile[];
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw new Error('Failed to fetch users');
  }
};

/**
 * Create new user profile
 * @param userId - Firebase Auth user ID
 * @param phoneNumber - User's phone number
 * @param displayName - Optional display name
 * @returns Created UserProfile
 */
export const createUserProfile = async (
  userId: string,
  phoneNumber: string,
  displayName?: string
): Promise<UserProfile> => {
  try {
    const now = Timestamp.now();
    
    const userProfile: Omit<UserProfile, 'id'> = {
      phoneNumber,
      displayName: displayName || `User ${phoneNumber.slice(-4)}`,
      addresses: [],
      createdAt: now,
      updatedAt: now,
      isAdmin: false,
      isActive: true,
      lastLoginAt: now,
    };

    await setDoc(doc(db, COLLECTIONS.USERS, userId), userProfile);

    return {
      id: userId,
      ...userProfile,
    };
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw new Error('Failed to create user profile');
  }
};

/**
 * Update user profile
 * @param userId - Firebase Auth user ID
 * @param updates - Partial user profile updates
 */
export const updateUserProfile = async (
  userId: string,
  updates: Partial<Omit<UserProfile, 'id' | 'createdAt'>>
): Promise<void> => {
  try {
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(doc(db, COLLECTIONS.USERS, userId), updateData);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw new Error('Failed to update user profile');
  }
};

/**
 * Update last login timestamp
 * @param userId - Firebase Auth user ID
 */
export const updateLastLogin = async (userId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      lastLoginAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    // Don't throw - this is not critical
  }
};

/**
 * Add new address to user profile
 * @param userId - Firebase Auth user ID
 * @param addressData - Address form data
 * @returns Created Address with ID
 */
export const addAddress = async (
  userId: string,
  addressData: AddressFormData
): Promise<Address> => {
  try {
    const newAddress: Address = {
      ...addressData,
      id: generateId(),
    };

    // If this is the first address or marked as default, update other addresses
    const userProfile = await getUserProfile(userId);
    
    if (userProfile) {
      let addresses = [...userProfile.addresses];
      
      // If new address is default, remove default from others
      if (newAddress.isDefault) {
        addresses = addresses.map(addr => ({ ...addr, isDefault: false }));
      }
      
      // If this is the first address, make it default
      if (addresses.length === 0) {
        newAddress.isDefault = true;
      }
      
      addresses.push(newAddress);
      
      await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
        addresses,
        updatedAt: Timestamp.now(),
      });
    }

    return newAddress;
  } catch (error) {
    console.error('Error adding address:', error);
    throw new Error('Failed to add address');
  }
};

/**
 * Update existing address
 * @param userId - Firebase Auth user ID
 * @param addressId - Address ID to update
 * @param updates - Address updates
 */
export const updateAddress = async (
  userId: string,
  addressId: string,
  updates: Partial<AddressFormData>
): Promise<void> => {
  try {
    const userProfile = await getUserProfile(userId);
    
    if (!userProfile) {
      throw new Error('User profile not found');
    }

    let addresses = userProfile.addresses.map(addr => {
      if (addr.id === addressId) {
        return { ...addr, ...updates };
      }
      // If updating to default, remove default from others
      if (updates.isDefault && addr.id !== addressId) {
        return { ...addr, isDefault: false };
      }
      return addr;
    });

    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      addresses,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating address:', error);
    throw new Error('Failed to update address');
  }
};

/**
 * Delete address from user profile
 * @param userId - Firebase Auth user ID
 * @param addressId - Address ID to delete
 */
export const deleteAddress = async (
  userId: string,
  addressId: string
): Promise<void> => {
  try {
    const userProfile = await getUserProfile(userId);
    
    if (!userProfile) {
      throw new Error('User profile not found');
    }

    const addresses = userProfile.addresses.filter(addr => addr.id !== addressId);

    // If deleted address was default, make first remaining address default
    if (addresses.length > 0 && !addresses.some(addr => addr.isDefault)) {
      addresses[0].isDefault = true;
    }

    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      addresses,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error deleting address:', error);
    throw new Error('Failed to delete address');
  }
};

/**
 * Set address as default
 * @param userId - Firebase Auth user ID
 * @param addressId - Address ID to set as default
 */
export const setDefaultAddress = async (
  userId: string,
  addressId: string
): Promise<void> => {
  try {
    const userProfile = await getUserProfile(userId);
    
    if (!userProfile) {
      throw new Error('User profile not found');
    }

    const addresses = userProfile.addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId,
    }));

    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      addresses,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error setting default address:', error);
    throw new Error('Failed to set default address');
  }
};

/**
 * Get or create user profile
 * Creates profile if it doesn't exist
 * @param userId - Firebase Auth user ID
 * @param phoneNumber - User's phone number
 * @returns UserProfile
 */
export const getOrCreateUserProfile = async (
  userId: string,
  phoneNumber: string
): Promise<UserProfile> => {
  try {
    let userProfile = await getUserProfile(userId);

    if (!userProfile) {
      try {
        userProfile = await createUserProfile(userId, phoneNumber);
      } catch (createError) {
        // A concurrent writer may have created the doc; re-read before failing
        userProfile = await getUserProfile(userId);
        if (!userProfile) throw createError;
      }
    } else {
      // Update last login
      await updateLastLogin(userId);
    }

    return userProfile;
  } catch (error) {
    console.error('Error getting or creating user profile:', error);
    throw new Error('Failed to get user profile');
  }
};
